import { Platform } from 'react-native';

import { formatInTimeZone } from 'date-fns-tz';

import { AddressTypes, type AddressType } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

/**
 * Returns the URL for the Momo survey.
 * Note that the `cels-4626-momo-rate-order-survey-enabled` flag contains either "Disabled" or the survey URL.
 * @see {@link https://go-momos.notion.site/Survey-URL-Query-Params-1ecef141e357801e8bb2c428c5b36755}
 */
export const useMomoSurveyUrl = (
  customer?: MomoSurveyUrlCustomer | null,
  order?: MomoSurveyUrlOrder | null,
) => {
  const momoSurveyUrl = useFeatureFlag('cels-4626-momo-rate-order-survey-enabled'); // prettier-ignore
  const restaurantEntityIdMapping = useFeatureFlag('cels-4651-momo-poc-restaurant-entity-id-mapping'); // prettier-ignore
  const usesOrderWebhook = useFeatureFlag('CELS-5513-momos-item-personalization') === true;

  if (momoSurveyUrl === disabled || !order) return null;

  const { orderId, orderTime, orderType, restaurantEntityId } = order;

  const outletId = restaurantEntityIdMapping?.[String(restaurantEntityId ?? '')]; // prettier-ignore
  const serviceType = orderType ? orderTypeToServiceType(orderType) : null;

  // With the order feed on, the link carries the id the feed sent, so Momos
  // can attach the order's items to the survey.
  const surveyOrderId = usesOrderWebhook
    ? buildMomosOrderId({
        source: orderType === AddressTypes['in-store'] ? 'brink' : 'gravy',
        storeId: restaurantEntityId,
        nativeOrderId: orderId,
      })
    : orderId;

  if (!outletId || !surveyOrderId || !orderTime || !serviceType) {
    return null;
  }

  try {
    const url = new URL(momoSurveyUrl);
    url.searchParams.set('serviceType', serviceType);
    url.searchParams.set('outletId', outletId);
    url.searchParams.set('name', getCustomerName(customer?.firstName, customer?.lastName));
    url.searchParams.set('email', customer?.email ?? '');
    url.searchParams.set('phoneNumber', customer?.phoneNumber ?? '');
    url.searchParams.set('orderId', surveyOrderId);
    url.searchParams.set('orderTime', toMomoTimestamp(orderTime));
    url.searchParams.set('momosChannel', Platform.OS === 'web' ? 'web' : 'app');

    if (usesOrderWebhook) {
      url.searchParams.set('source', orderWebhookSource);
    }

    return url.toString();
  } catch {
    return null;
  }
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * The order id Momos stores for the order feed: `<source>:<storeId>:<nativeOrderId>`.
 * Brink issues in-store ids; Gravy issues every other order's id. loyalty-svc
 * builds the same value (momos-order-items.serializer); keep the two in step.
 */
export function buildMomosOrderId(params: {
  source: 'brink' | 'gravy';
  storeId?: string | number | null;
  nativeOrderId?: string | number | null;
}): string | null {
  const storeId = String(params.storeId ?? '').trim();
  const nativeOrderId = String(params.nativeOrderId ?? '').trim();

  if (!/^[1-9]\d*$/.test(storeId)) return null;
  if (!nativeOrderId || nativeOrderId.includes(':')) return null;

  return `${params.source}:${storeId}:${nativeOrderId}`;
}

const getCustomerName = (firstName?: string | null, lastName?: string | null) =>
  `${firstName ?? ''} ${lastName ?? ''}`.trim();

const orderTypeToServiceType = (orderType: AddressType): MomosServiceType | null => {
  switch (orderType) {
    case AddressTypes.pickup:
      return 'pickup';
    case AddressTypes.delivery:
      return 'delivery';
    case AddressTypes.kiosk:
    case AddressTypes['in-store']:
      return 'dinein';
    case AddressTypes.outpost:
      return 'outpost';
    default:
      return null;
  }
};

/**
 * Date and time of the order in the format YYYY-MM-DDThh24:mi:ssZ
 */
export function toMomoTimestamp(date: string) {
  return formatInTimeZone(new Date(date), 'UTC', "yyyy-MM-dd'T'HH:mm:ss'Z'");
}

// ─── Constants ───────────────────────────────────────────────────────────────

const disabled = 'Disabled' as const;

/** Tells Momos to look up the order the feed sent under this survey's order id. */
const orderWebhookSource = 'ORDER_WEBHOOK';

// ─── Types ───────────────────────────────────────────────────────────────────

type MomosServiceType = 'dinein' | 'pickup' | 'delivery' | 'outpost';

type MomoSurveyUrlCustomer = {
  phoneNumber?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

type MomoSurveyUrlOrder = {
  orderId?: string | null;
  orderTime?: string | null;
  orderType?: AddressType | null;
  restaurantEntityId?: string | number | null;
};
